import axios from 'axios';
import { wrapAxios } from '../../axios';
import {
  ICommonDomain,
  ICommonDomainCheckResponse,
  IDNSRecord,
  IEditCommonDomainRequest,
  IEditNameserverRequest,
  IDNSSEC,
  IChangeDomainRegistrantRequest,
  IDnsUpdateKey,
  ICreatedDnsUpdateKey,
} from '@cuspar/ywt.ch.model';

export type TransferCheckOptions = { quiet?: boolean; timeoutMs?: number };

export const domainAPI = {
  changeRegistrant: async (domain: string, body: IChangeDomainRegistrantRequest) => {
    return wrapAxios(() => axios.put<ICommonDomain>(`/domains/${encodeURIComponent(domain)}/registrant`, body));
  },
  checkDomainAvailability: async (domain: string) => {
    return wrapAxios(() => axios.get<ICommonDomainCheckResponse[]>(`/domains/${domain}/check`));
  },
  checkDomainsAvailability: async (domains: string[]) => {
    return wrapAxios(() => axios.get<ICommonDomainCheckResponse[]>(`/domains/availability?domains=${domains.join(',')}`));
  },
  /**
   * The batch importer passes `quiet` (per-row status instead of toasts and Sentry noise)
   * and a `timeoutMs` bound so one hung registry lookup cannot stall its sequential run.
   * The single-domain form keeps the default: no timeout, toast and Sentry capture on error.
   */
  checkDomainTransferAvailability: async (domain: string, authCode: string, options: TransferCheckOptions = {}) => {
    return wrapAxios(() => axios.post<{ available: boolean; reason?: string }>(`/domains/${encodeURIComponent(domain)}/transfer/availability`, { authCode }, { timeout: options.timeoutMs }), {
      showError: !options.quiet,
      captureServerErrors: !options.quiet,
    });
  },
  getMyDomains: async () => {
    return wrapAxios(() => axios.get<ICommonDomain[]>(`/domains/mydomains`));
  },
  getDomain: async (domain: string) => {
    return wrapAxios(() => axios.get<ICommonDomain>(`/domains/${domain}`));
  },
  editDomain: async (domain: string, body: IEditCommonDomainRequest, quiet = false) => {
    return wrapAxios(() => axios.put<ICommonDomain>(`/domains/${encodeURIComponent(domain)}`, body, { timeout: 30000 }), { showError: !quiet });
  },
  listDNSRecords: async (domain: string) => {
    return wrapAxios(() => axios.get<IDNSRecord[]>(`/domains/${domain}/records`));
  },
  upsertDNSRecord: async (domain: string, body: IDNSRecord) => {
    return wrapAxios(() => axios.put<IDNSRecord>(`/domains/${domain}/records`, body));
  },
  deleteDNSRecord: async (domain: string, body: IDNSRecord) => {
    return wrapAxios(() => axios.delete<IDNSRecord>(`/domains/${domain}/records`, { data: body }));
  },
  changeNameserver: async (domain: string, nameserver: IEditNameserverRequest) => {
    return wrapAxios(() => axios.put<ICommonDomain>(`/domains/${domain}/nameserver`, nameserver));
  },
  syncDomainFromEppToDb: async (domain: string) => {
    return wrapAxios(() => axios.post<ICommonDomain>(`/domains/${domain}/sync`));
  },
  scanDNSRecords: async (domain: string, hosts?: string[]) => {
    const query = Array.isArray(hosts) && hosts.length > 0 ? `?hosts=${encodeURIComponent(hosts.join(','))}` : '';
    return wrapAxios(() => axios.get<IDNSRecord[]>(`/domains/${domain}/scan${query}`));
  },
  listDnsUpdateKeys: async (domain: string) => {
    return wrapAxios(() => axios.get<IDnsUpdateKey[]>(`/domains/${encodeURIComponent(domain)}/dns-update-keys`));
  },
  createDnsUpdateKey: async (domain: string, label: string) => {
    return wrapAxios(() => axios.post<ICreatedDnsUpdateKey>(`/domains/${encodeURIComponent(domain)}/dns-update-keys`, { label }));
  },
  revokeDnsUpdateKey: async (domain: string, name: string) => {
    return wrapAxios(() => axios.delete<void>(`/domains/${encodeURIComponent(domain)}/dns-update-keys/${encodeURIComponent(name)}`));
  },
  addDnssecRecord: async (domain: string, body: IDNSSEC) => {
    return wrapAxios(() => axios.post<ICommonDomain>(`/domains/${domain}/dnssec/records`, body));
  },
  removeDnssecRecord: async (domain: string, body: IDNSSEC) => {
    return wrapAxios(() => axios.delete<ICommonDomain>(`/domains/${domain}/dnssec/records`, { data: body }));
  },
  transferOut: async (domain: string) => {
    return wrapAxios(() => axios.post<{ success: boolean; reason?: string }>(`/domains/${domain}/transfer-out`));
  },
  removeDomain: async (domain: string) => {
    return wrapAxios(() => axios.delete<void>(`/domains/${domain}`));
  },
};
